import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useFeatureFlags } from '../../selectors/app-config-selectors'
import { FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'
import transformFullCreditApplicationFromApi from '../full-credit-application/full-credit-application-transform'
import { FraudFlagChangeDto, fraudApi, FraudFlagsResponse, UpdateFlagRequest } from './fraud-api'

export const FRAUD_FLAGS_QUERY_KEY = 'fraudFlags';

export const useFraudFlags = (financingProgramId: string, creditApplicationId: string, enabled = true): [FraudFlagsResponse | null, boolean, Error | null] => {
  const featureFlags = useFeatureFlags();

  const { data, isFetching, error } = useQuery<FraudFlagsResponse, Error>({
    queryKey: [FRAUD_FLAGS_QUERY_KEY, financingProgramId, creditApplicationId],
    queryFn: () => fraudApi.getFraudFlags(financingProgramId, creditApplicationId),
    enabled: !!creditApplicationId && !!financingProgramId && featureFlags.useFraudFlags && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  return [data ?? null, isFetching, error];
};

export const useFraudFlagHistory = (financingProgramId: string, creditApplicationId: string) => {
  const featureFlags = useFeatureFlags();

  return useQuery<FraudFlagChangeDto[], Error>({
    queryKey: [FRAUD_FLAGS_QUERY_KEY, 'history', financingProgramId, creditApplicationId],
    queryFn: () => fraudApi.getFlagHistory(financingProgramId, creditApplicationId),
    enabled: !!creditApplicationId && !!financingProgramId && featureFlags.useFraudFlags,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

interface UpdateFraudFlagParams {
  financingProgramId: string;
  creditApplicationId: string;
  indicatorType: string;
  flagKey: string;
  request: UpdateFlagRequest;
}

export const useUpdateFraudFlag = (): [MutationFunction<void, UpdateFraudFlagParams>, boolean] => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      financingProgramId,
      creditApplicationId,
      indicatorType,
      flagKey,
      request,
    }: UpdateFraudFlagParams) => fraudApi.updateFlag(financingProgramId, creditApplicationId, indicatorType, flagKey, request),
    onMutate: async (variables) => {
      // Cancel outgoing refetches pour éviter les conflits
      await queryClient.cancelQueries({
        predicate: (query) => {
          return Array.isArray(query.queryKey) &&
            query.queryKey[0] === FRAUD_FLAGS_QUERY_KEY &&
                 query.queryKey[1] === variables.financingProgramId &&
                 query.queryKey[2] === variables.creditApplicationId;
        },
      });

      // Optimistic update
      const previousData = queryClient.getQueriesData({
        predicate: (query) => {
          return Array.isArray(query.queryKey) &&
            query.queryKey[0] === FRAUD_FLAGS_QUERY_KEY &&
                 query.queryKey[1] === variables.financingProgramId &&
                 query.queryKey[2] === variables.creditApplicationId;
        },
      });

      // Update optimistically
      queryClient.setQueriesData<FraudFlagsResponse>({
        predicate: (query) => {
          return Array.isArray(query.queryKey) &&
            query.queryKey[0] === FRAUD_FLAGS_QUERY_KEY &&
                 query.queryKey[1] === variables.financingProgramId &&
                 query.queryKey[2] === variables.creditApplicationId;
        },
      }, (old) => {
        if (!old) return old;
        return {
          ...old,
          flags: {
            ...old.flags,
            [variables.indicatorType]: old.flags[variables.indicatorType].map(flag =>
            flag.key === variables.flagKey
                ? { ...flag, value: variables.request.value, reason: variables.request.reason, isAutomatic: false }
                : flag)
          }
        };
      });

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: (_, __, variables) => {
      // Refetch to ensure server state
      void queryClient.invalidateQueries({
        predicate: (query) => {
          return Array.isArray(query.queryKey) &&
            query.queryKey[0] === FRAUD_FLAGS_QUERY_KEY &&
            (
              (query.queryKey[1] === variables.financingProgramId && query.queryKey[2] === variables.creditApplicationId) ||
              (query.queryKey[1] === 'history' && query.queryKey[2] === variables.financingProgramId && query.queryKey[3] === variables.creditApplicationId)
            );
        },
      });
    },
  });
  return [mutateAsync, isPending];
};

interface CalculateAutomaticFlagsParams {
  financingProgramId: string;
  creditApplicationId: string;
}

export const useCalculateAutomaticFlags = (): [MutationFunction<void, CalculateAutomaticFlagsParams>, boolean] => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ financingProgramId, creditApplicationId }: CalculateAutomaticFlagsParams) =>
      fraudApi.calculateAutomaticFlags(financingProgramId, creditApplicationId),
    onSuccess: (_, { financingProgramId, creditApplicationId }) => {
      // Invalider toutes les requêtes fraud flags pour ce creditApplicationId
      void queryClient.invalidateQueries({
        predicate: (query) => {
          return Array.isArray(query.queryKey) &&
            query.queryKey[0] === FRAUD_FLAGS_QUERY_KEY &&
                 query.queryKey[1] === financingProgramId &&
                 query.queryKey[2] === creditApplicationId;
        },
      });
    },
  });
  return [mutateAsync, isPending];
};

interface LaunchFraudIQParams {
  financingProgramId: string;
  creditApplicationId: string;
}

export const useLaunchFraudIQ = (): [MutationFunction<FullCreditApplication, LaunchFraudIQParams>, boolean] => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ financingProgramId, creditApplicationId }: LaunchFraudIQParams) => {
      const dto = await fraudApi.launchFraudIQAnalysis(financingProgramId, creditApplicationId);
      return transformFullCreditApplicationFromApi(dto);
    },
    onSuccess: (fullCreditApp, { creditApplicationId }) => {
      queryClient.setQueriesData<FullCreditApplication>(
        {
          predicate: (query) => {
            return Array.isArray(query.queryKey) &&
              query.queryKey[0] === fullCreditApplicationKeys.all[0] &&
              query.queryKey[2] === creditApplicationId;
          },
        },
        () => fullCreditApp,
      );
    },
  });
  return [mutateAsync, isPending];
};
